import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

// "(source: deck.pdf)" and "— to confirm" read as small notes after a line, the way the design shows a
// claim's source; the text itself is unchanged. A note stays whole, moving to its own line when it doesn't
// fit. One longer than a line of the card (a note naming several files) starts its own line and wraps
// within the card instead of overflowing; about 98 caption characters fit a line at 1280px. The break
// before it is drawn, not typed, because italics in a file name split a note into pieces a style can't
// keep together; for the same reason the gap before a note sits on its first character, not on every
// piece, and `data-note` lets the editor size an italic piece like the rest of the note.
const NOTE = /\(source:[^)]*\)|— to confirm/g;
const NOTE_CLASS = "typo-caption whitespace-nowrap text-muted-foreground";
const LONG_NOTE_CHARS = 90;
const LONG_NOTE_CLASS = "typo-caption text-muted-foreground";
const NOTE_START_CLASS = "ms-1";

const lineBreak = () => {
  const element = document.createElement("span");
  element.className = "block";
  element.contentEditable = "false";
  return element;
};

const editingKey = new PluginKey<boolean>("caseFrameEditing");

export const CaseFrameEditorMarks = Extension.create({
  name: "caseFrameEditorMarks",
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("caseFrameNotes"),
        props: {
          decorations(state) {
            const decorations: Decoration[] = [];
            state.doc.descendants((node, pos) => {
              if (!node.isTextblock) return true;
              for (const match of node.textContent.matchAll(NOTE)) {
                const from = pos + 1 + (match.index ?? 0);
                const long = match[0].length > LONG_NOTE_CHARS;
                if (long) {
                  decorations.push(
                    Decoration.widget(from, lineBreak, { side: -1, ignoreSelection: true, key: "note-break" })
                  );
                }
                decorations.push(
                  Decoration.inline(from, from + match[0].length, {
                    class: long ? LONG_NOTE_CLASS : NOTE_CLASS,
                    "data-note": ""
                  })
                );
                decorations.push(Decoration.inline(from, from + 1, { class: NOTE_START_CLASS }));
              }
              return false;
            });
            return DecorationSet.create(state.doc, decorations);
          }
        }
      }),
      // The line being edited is marked, like the design's claim while it is edited.
      new Plugin({
        key: editingKey,
        state: {
          init: () => false,
          apply: (tr, focused: boolean) => (tr.getMeta(editingKey) as boolean | undefined) ?? focused
        },
        props: {
          handleDOMEvents: {
            focus: (view) => {
              view.dispatch(view.state.tr.setMeta(editingKey, true));
              return false;
            },
            blur: (view) => {
              view.dispatch(view.state.tr.setMeta(editingKey, false));
              return false;
            }
          },
          decorations(state) {
            if (!editingKey.getState(state)) return null;
            const { $head } = state.selection;
            for (let depth = $head.depth; depth >= 1; depth--) {
              if (depth === 1 || $head.node(depth).type.name === "listItem") {
                return DecorationSet.create(state.doc, [
                  Decoration.node($head.before(depth), $head.after(depth), { class: "is-editing" })
                ]);
              }
            }
            return null;
          }
        }
      })
    ];
  }
});
